import React from 'react';
import classNames from 'classnames';
import Box from 'sail/Box';
import Toggle, {ToggleItem} from 'sail/Toggle';
import Select from 'sail/Select';
import Group from 'sail/Group';
import {BodyAlt} from 'sail/Text';
import {defineMessages, FormattedMessage} from 'react-intl';
import {useGuide} from '../data/GuideProvider';
import {useContent} from '../data/ContentProvider';

import {formatLanguage} from '../util';
import '../css/Settings.scss';

const messages = defineMessages({
  country: {
    id: 'docsApp.integrationBuilder.settings.country',
    description: 'Country label',
    defaultMessage: 'Country:',
  },
  reader: {
    id: 'docsApp.integrationBuilder.settings.reader',
    description: 'Reader label',
    defaultMessage: 'Reader:',
  },
  platform: {
    id: 'docsApp.integrationBuilder.settings.platform',
    description: 'Platform label',
    defaultMessage: 'Platform:',
  },
  frontend: {
    id: 'docsApp.integrationBuilder.settings.frontend',
    description: 'Frontend label',
    defaultMessage: 'Frontend:',
  },
  backend: {
    id: 'docsApp.integrationBuilder.settings.backend',
    description: 'Backend label',
    defaultMessage: 'Backend:',
  },
});

const Selector = ({label, type, activeOption, options}) => {
  const {changeSetting} = useGuide();
  const {integrationType} = useContent();
  return (
    <Box flex={{alignItems: 'center'}}>
      <BodyAlt color="dark" style={{marginRight: 12}}>
        {label}
      </BodyAlt>
      <Box className={classNames('Selector--full', integrationType)}>
        <Toggle name={type} type="radio">
          {options.map((option) => {
            return (
              <ToggleItem
                key={option}
                label={formatLanguage(option)}
                checked={option === activeOption}
                onChange={() => {
                  changeSetting({[type]: option});
                }}
              />
            );
          })}
        </Toggle>
      </Box>
      <Box className={classNames('Selector--small', integrationType)}>
        <Select
          value={activeOption}
          onChange={(option) => {
            changeSetting({[type]: option});
          }}
        >
          {options.map((option) => (
            <option key={option} value={option}>
              {formatLanguage(option)}
            </option>
          ))}
        </Select>
      </Box>
    </Box>
  );
};

const CountrySelector = () => {
  const {languages, changeSetting} = useGuide();
  const {integrationType} = useContent();
  // Only show the country selector if we have more than one country option
  if (languages.allowed && !languages.allowed.country) {
    return null;
  }

  const {country: countries} = languages.allowed;
  const {country: activeCountry} = languages.active;
  return (
    <Box flex={{alignItems: 'center'}}>
      <BodyAlt color="dark" style={{marginRight: 12}}>
        <FormattedMessage {...messages.country} />
      </BodyAlt>
      <Box className={classNames('Selector', integrationType)}>
        <Select
          value={activeCountry}
          options={countries.map((option) => {
            return {
              label: formatLanguage(option),
              value: option,
            };
          })}
          onChange={(option) => {
            changeSetting({['country']: option});
          }}
        />
      </Box>
    </Box>
  );
};

const ReaderSelector = () => {
  const {languages, changeSetting} = useGuide();
  const {integrationType} = useContent();
  // Only show the reader selector if we have more than one reader option
  if (languages.allowed && !languages.allowed.reader) {
    return null;
  }

  const {reader: allowedReaders} = languages.allowed;
  const {reader: activeReader} = languages.active;
  const readers = allowedReaders[languages.active.country];
  return (
    <Box flex={{alignItems: 'center'}}>
      <BodyAlt color="dark" style={{marginRight: 12}}>
        <FormattedMessage {...messages.reader} />
      </BodyAlt>
      <Box className={classNames('Selector', integrationType)}>
        <Select
          value={activeReader}
          options={readers.map((option) => {
            return {
              label: formatLanguage(option),
              value: option,
            };
          })}
          onChange={(option) => {
            changeSetting({['reader']: option});
          }}
        />
      </Box>
    </Box>
  );
};

const PlatformSelector = () => {
  const {languages} = useGuide();
  // Only show the platform selector if we have more than one platform option
  if (!languages.allowed?.platform || languages.allowed.platform.length < 2) {
    return null;
  }
  const {platform: allowedPlatforms} = languages.allowed;
  const {platform: activePlatform} = languages.active;
  const platforms = languages.active?.reader
    ? allowedPlatforms[languages.active?.reader][languages.active?.country]
    : allowedPlatforms;
  return (
    <Selector
      label={<FormattedMessage {...messages.platform} />}
      type="platform"
      activeOption={activePlatform}
      options={platforms}
    />
  );
};

const ClientSelector = () => {
  const {languages} = useGuide();
  if (languages.allowed && !languages.allowed.client) {
    return null;
  }

  const {client: allowedClients} = languages.allowed;
  const {client: activeClient} = languages.active;
  const clients = allowedClients[languages.active.platform];
  return (
    <Selector
      label={<FormattedMessage {...messages.frontend} />}
      type="client"
      activeOption={activeClient}
      options={clients}
    />
  );
};

const ServerSelector = () => {
  const {languages} = useGuide();

  if (!languages.allowed?.server || languages.allowed.server.length < 2) {
    return null;
  }

  const {server: servers} = languages.allowed;
  const {server: activeServer} = languages.active;
  return (
    <Selector
      label={<FormattedMessage {...messages.backend} />}
      type="server"
      activeOption={activeServer}
      options={servers}
    />
  );
};

export default ({toggleExpand, expanded, setHeight}) => {
  const {integrationType} = useContent();

  return (
    <Box
      className={classNames('Settings', integrationType)}
      flex={{justifyContent: 'space-between', alignItems: 'center'}}
      margin={{bottom: 32, left: 32}}
    >
      <Group direction="horizontal" alignItems="center" spacing={24}>
        <CountrySelector />
        <ReaderSelector />
        <PlatformSelector />
        <ClientSelector />
        <ServerSelector />
      </Group>
    </Box>
  );
};
